import React, { useEffect, useRef, useState } from 'react';
import { PlusIcon } from '@radix-ui/react-icons';

import { commitPatches } from '../interaction/commit-span';
import { isAddDisabledFor } from '../interaction/drag-math';
import { dateShort2 } from '../../../assets/contants/dates-fns';
import { formatPlainDate, plainDateFromEpochDay } from '../../../core/services/plain-date';
import { Tooltip } from '../../tooltip/Tooltip';
import styles from './empty-row-add.module.scss';

import type { GanttPatch, Row, TimeScale } from '../types';

export type EmptyRowAddProps = {
  row: Row;
  /** The chart's shared day<->pixel scale — `scale.dayAt` is the ONLY way the hovered day is
   * derived (see the handler below); never a second, hand-rolled `mouseX / pxPerDay`. */
  scale: TimeScale;
  /** Same coordinate origin `row-list.tsx` writes `--bar-offset`/`--bar-span` from, and the one
   * `xOf`/`dayAt` are both defined against. */
  windowStartDay: number;
  /** `GanttConfig.readOnly`, folded into `isAddDisabledFor` alongside `row.isGroup`/
   * `row.item.editable` — see that function's doc-comment for why it is not `isDragDisabledFor`. */
  readOnly?: boolean;
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
};

/**
 * Restores `gantt-chart/components/empty-chart-row.tsx`'s hovering `+`: an undated row's track
 * renders nothing today (`Bar` returns `null` without a span), so this is the only way to give one
 * dates at all. `row-list.tsx` mounts this in place of `Bar` for exactly the rows `Bar` refuses.
 *
 * The button stays mounted at all times — reachable by Tab — rather than appearing only inside a
 * `showAddStrip &&` block the way the old component did. An element removed from the DOM by a
 * hover-only condition is also removed from the tab order, so a keyboard-only user would have
 * nothing to land focus on; see `empty-row-add.module.scss`'s `.addButton` for how visibility is
 * done instead (opacity + `pointer-events`, not `display`/unmounting).
 */
export const EmptyRowAdd: React.FC<EmptyRowAddProps> = ({
  row,
  scale,
  windowStartDay,
  readOnly,
  onCommit,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  const [committing, setCommitting] = useState(false);

  // Mirrors `useBarDrag`/`useBarKeyboard`'s identical guard: `onCommit` may return a promise, and
  // this row unmounts the moment the commit actually lands (the item now has a span, so
  // `row-list.tsx` renders `Bar` here instead on the next render) — a late `setCommitting(false)`
  // must not write into that unmounted instance.
  //
  // The `true` on SETUP is as load-bearing as the `false` on teardown, for the same reason as in
  // `use-span-preview.ts`: StrictMode and `<Activity>`/Offscreen both unmount effects while
  // KEEPING state and refs, so a ref that is only ever written `false` stays `false` for the whole
  // second life of the component — and `setCommitting(false)` becomes a permanent no-op, wedging
  // the button disabled after its first commit. Inert today (nothing in `apps/webapp/src` renders
  // a `StrictMode`), and a live bug the day something does.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Computed on every render regardless of eligibility, same as `Bar`'s own `disabled` — cheap,
  // and keeps every hook above called unconditionally (Rules of Hooks) whether or not this row
  // ends up rendering anything.
  const disabled = isAddDisabledFor(row, readOnly);
  if (disabled) return null;

  // No pointer position yet (mount, or reached by keyboard alone): the row's own window start is
  // the only day this component has any basis to default to.
  const day = hoveredDay ?? windowStartDay;
  const visible = hoveredDay !== null || focused;
  const x = scale.xOf(day, windowStartDay);
  const date = plainDateFromEpochDay(day);
  const label = formatPlainDate(date, dateShort2);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // The inverse `xOf` already owns — never a second, hand-rolled division. Using anything else
    // here is exactly how the header and this affordance would drift on which day a pixel means.
    setHoveredDay(scale.dayAt(event.clientX - rect.left, windowStartDay));
  };

  const handleMouseLeave = (): void => setHoveredDay(null);

  const handleClick = (): void => {
    if (committing) return;

    // No `patchesFor` diff here: `isAddDisabledFor` already guarantees `row.span` is `undefined`
    // for every row this component renders on, so both fields are unconditionally needed — there
    // is no real "previous span" to compute a partial patch against. Building the patch directly
    // and routing it through `commitPatches` (the same skip/invoke/settle tail `commitSpan` uses)
    // keeps the one shared commit sequence without leaning on a synthetic prior span.
    setCommitting(true);
    commitPatches({
      patches: [{ id: row.item.id, startDate: date, dueDate: date }],
      onCommit,
      onSettle: () => {
        if (mountedRef.current) setCommitting(false);
      },
    });
  };

  return (
    <div
      ref={containerRef}
      className={styles['hitLayer']}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip text={label} disabled={!visible}>
        <button
          type="button"
          data-testid="empty-row-add"
          className={styles['addButton']}
          data-visible={visible}
          style={{ left: `${x}px` }}
          disabled={committing}
          aria-label={`Add dates for ${row.item.name} on ${label}`}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onClick={handleClick}
        >
          <span className={styles['content']}>
            <PlusIcon color="var(--color-grayscale-white)" />
          </span>
        </button>
      </Tooltip>
    </div>
  );
};
